/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnChanges} from '@angular/core';
import * as moment from 'moment/moment';

import {AbsenceEvent} from './calendar-absence.component';

@Component({
  selector: 'gp-calendar-pending-absence',
  template: `
    <div *ngIf="eventOpts?.view"
         style="width: calc(100% - 4px);"
         [style.height]="height"
         [popover]="dayPop"
         container="body"
         [popoverTitle]="eventOpts.view.transId | translate"
         triggers="mouseenter:mouseleave">
      <div class="borders"
           [style.borderColor]="eventOpts.view.borderColor"
           style="height: 0;"
           [style.width]="isTeamPlanning ? 'calc(100% - 4px)' : 'calc(100% - 6px)'"
           [style.left]="isTeamPlanning ? 0 : 'initial'">
      </div>
      <div class="borders"
           [style.borderColor]="eventOpts.view.borderColor"
           style="left: 0; width: 0" [style.height]="height">
      </div>
      <div class="borders"
           [style.borderColor]="eventOpts.view.borderColor"
           style="height: 0;"
           [style.width]="isTeamPlanning ? '100%' : 'calc(100% - 6px)'"
           [style.top]="height"
           [style.left]="isTeamPlanning ? 0 : 'initial'">
      </div>
      <div class="borders"
           [style.borderColor]="eventOpts.view.borderColor"
           style="right: 0; width: 0;"
           [style.height]="height">
      </div>
      <ng-template #dayPop>
        <ul class="list-unstyled" style="min-width: 130px" *ngIf="!isTeamPlanning">
          <li class="nowrap" *ngIf="eventOpts.attributes.hoursWorked">
            {{ 'PAYROLL_PREPARE.hours_worked' | translate }}: {{ eventOpts.attributes.hoursWorked }}
          </li>
          <li class="nowrap" *ngIf="eventOpts.attributes.hoursPaid">
            {{ 'PAYROLL_PREPARE.hours_paid' | translate }}: {{ eventOpts.attributes.hoursPaid }}
          </li>
          <li class="nowrap">
            {{ 'PAYROLL_PREPARE.start_date' | translate }}: {{ eventOpts.attributes.begin | gpDate }}
          </li>
          <li class="nowrap">
            {{ 'PAYROLL_PREPARE.end_date' | translate }}: {{ eventOpts.attributes.end | gpDate }}
          </li>
          <li class="nowrap">
            {{ 'PAYROLL_PREPARE.duration_of_hours' | translate:{hours: getDuration()} }}
          </li>
          <li class="nowrap" *ngIf="eventOpts.attributes.status">
            {{'Status' | translate}}: {{ getStatusLabel(eventOpts.attributes.status) | translate }}
          </li>
        </ul>
      </ng-template>
    </div>
  `,
  styles: [`
    .borders {
      border-style: solid;
      border-width: 2px;
      top: 0;
      position: absolute;
      z-index: 1;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CalendarPendingAbsenceComponent implements OnChanges {

  @Input()
  public isReadOnly: boolean = true;

  public height: string;
  public marginTop: string;
  public isHover: boolean = false;
  public isBegining: boolean;
  public isEnding: boolean;
  public isSingleDay: boolean;
  public cellHeight: number = 15;

  @Input('event')
  public eventOpts: AbsenceEvent;

  @Input('weekview')
  public weekview: boolean = false;

  @Input('isTeamPlanning')
  public isTeamPlanning: boolean = false;

  // it should be 8 for month view
  @Input('workedHours')
  public workedHours: number = 8;

  @Input('color')
  public color: string = '#fff';

  @Input('date')
  public date: string;

  constructor(public element: ElementRef) {
  }

  public ngOnChanges() {
    if (this.eventOpts) {
      if (!this.isTeamPlanning) {
        this.cellHeight = this.weekview ? 43.3 : 15;
      } else {
        this.cellHeight = 12;
      }
      this.isBegining = moment(this.date).isSame(this.eventOpts.attributes.begin);
      this.isEnding = moment(this.date).isSame(this.eventOpts.attributes.end);
      this.isSingleDay = this.eventOpts.attributes.begin === this.eventOpts.attributes.end;
      if (this.isSingleDay) {
        if (this.eventOpts.attributes.beginDayfactor + this.eventOpts.attributes.endDayfactor > 1) {
          this.eventOpts.attributes.endDayfactor = 0;
        }
      }
      this.updateEventView();
    }
  }

  public getDuration(): string {
    return this.eventOpts.view.duration.toFixed(1);
  }

  public updateEventView() {
    this.marginTop = '0';
    let height: number = 100; // percents
    let marginTop = 0;

    if (this.isBegining) {
      height -= this.eventOpts.attributes.beginDayfactor * 100;
      marginTop = this.eventOpts.attributes.beginDayfactor * this.workedHours * this.cellHeight;
      if (!this.weekview && marginTop > 100) {
        marginTop = 100;
      }
      this.marginTop = `${marginTop}px`;
    }
    if (this.isEnding) {
      height -= this.eventOpts.attributes.endDayfactor * 100;
    }

    height = height * this.workedHours * this.cellHeight / 100;
    this.height = `${this.isTeamPlanning ? height : height - 4}px`;

    this.element.nativeElement.style.marginTop = this.marginTop;
    // this.element.nativeElement.style.height = this.height;
  }

  public getStatusLabel(status: string): string {
    switch (status) {
      case 'CREATE':
        return 'pending creation';
      case 'UPDATE':
        return 'pending update';
      case 'CANCELLATION':
        return 'pending cancellation';
      default:
        return '-';
    }
  }

  @HostListener('mouseenter')
  public mouseenter() {
    this.isHover = true;
  }

  @HostListener('mouseleave')
  public mouseleave() {
    this.isHover = false;
  }
}
